<template>
  <div v-if="organisation">
    <h1>{{ organisation ? organisation.code : 'Organisation' }} - Admin</h1>

    <!-- Organisation details -->

    <h2 v-if="organisation">Organisation details</h2>
    <dl v-if="organisation" class="row">
      <dt class="col-sm-3">Code</dt>
      <dd class="col-sm-9">{{ organisation.code }}</dd>
      <dt class="col-sm-3">Name</dt>
      <dd class="col-sm-9">{{ organisation.name }}</dd>
    </dl>

    <!-- Edit organisation details button - To be implemented -->
    <button v-if="organisation" type="button" class="btn btn-primary mb-3 btn-sm" @click="showEditOrganisationModal"
            disabled>Edit
      organisation details
    </button>
    <p v-else>Loading...</p>

    <!-- Sub-Navigation -->
    <div id="subNavigation" class="mb-3">
      <button type="button" class="btn btn-primary me-3" @click="navigateToUserShiftsAdmin">Manage User Shifts</button>
      <button type="button" class="btn btn-primary me-3" @click="navigateToGoodiesAdmin">Manage goodies</button>
      <button type="button" class="btn btn-primary me-3" @click="navigateToUserGoodiesAdmin">Manage user goodies
      </button>
    </div>

    <!-- User roles in this organisation -->
    <div id="userRolesSection">
      <h2>Roles</h2>
      <button type="button" class="btn btn-primary mb-3" @click="navigateToRoles">Manage roles</button>
    </div>

    <!-- Users -->
    <div id="usersSection">
      <h2>Users</h2>
      <button type="button" class="btn btn-primary mb-3" @click="navigateToUsers">Manage Users</button>
    </div>

    <!-- Sites -->
    <div class="card mb-3">
      <h2 class="card-header">Sites</h2>
      <div class="card-body">
        <ul class="list-group list-group-flush p-0 m-0"
            v-if="organisation && organisation.sites.sort((a, b) => a.code.localeCompare(b.code))">
          <li class="list-group-item p-1" v-for="site in organisation.sites" :key="site.id">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ site.code }}</strong> - {{ site.name }}
              </div>
              <div>
                <button class="btn btn-primary btn-sm me-2" v-if="isAdmin" @click="editSite(organisation, site)"><i
                    class="fas fa-pen"></i></button>
                <button class="btn btn-danger btn-sm me-2" v-if="isAdmin" @click="confirmDeleteSite(site.id)"><i
                    class="fas fa-trash"></i></button>
                <button class="btn btn-primary btn-sm" v-if="isAdmin" @click="createLocationModal(site)"><i
                    class="fas fa-plus"></i> Loc
                </button>
              </div>
            </div>
            <EditSiteModal
                v-if="showEditSiteModal"
                :organisation="selectedOrganisation"
                :site="selectedSite"
                @close="showEditSiteModal = false"
                @save-site="updateSite"
            />
          </li>
        </ul>
        <button class="btn btn-primary btn-sm" v-if="isAdmin" @click="showCreateSiteModal = true">Add site</button>
      </div>
      <CreateSiteModal
          v-if="showCreateSiteModal"
          :organisation="organisation"
          @close="showCreateSiteModal = false"
          @create-site="createSite"
      />
    </div>

    <!-- Locations -->
    <div class="card mb-3" v-if="sortedLocations.length>0">
      <h2 class="card-header">Locations</h2>
      <div class="card-body">
        <ul class="list-group list-group-flush p-0 m-0" v-if="organisation && organisation.sites">
          <li class="list-group-item p-1"
              v-for="location in sortedLocations"
              :key="location.id">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                {{ location.site.code }}.<strong>{{ location.code }}</strong> - {{ location.name }}
              </div>
              <div>
                <button class="btn btn-primary btn-sm me-2" v-if="isAdmin" @click="editLocation(location)"><i
                    class="fas fa-pen"></i></button>
                <button class="btn btn-danger btn-sm" v-if="isAdmin" @click="confirmDeleteLocation(location.id)"><i
                    class="fas fa-trash"></i></button>
                <button v-if="organisation.timeslots_enabled && isAdmin" class="btn btn-primary btn-sm ms-2"
                        @click="createSubLocationModal(location)"><i
                    class="fas fa-plus"></i> SubL
                </button>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <CreateLocationModal
        v-if="showCreateLocationModal"
        :siteId="selectedSite.id"
        @close="showCreateLocationModal = false"
        @create-location="addLocation"
    />
    <EditLocationModal
        v-if="showEditLocationModal"
        :location="selectedLocation"
        :organisationSlug="this.$route.params.slug"
        @close="showEditLocationModal = false"
        @save-location="updateLocation"
    />

    <!-- CreateShiftModal component outside the (Events) loop -->
    <CreateShiftModal
        v-if="showCreateShiftModal"
        :event="selectedEvent"
        :locations="organisation.sites.flatMap(site => site.locations)"
        :organisation_shifts="organisation.events.flatMap(event => event.shifts)"
        :tags="tags"
        @close="showCreateShiftModal = false"
        @create-shift="createShift"
    />

    <!-- SubLocations -->
    <div class="card mb-3" v-if="organisation.flat_sub_locations && organisation.flat_sub_locations.length>0">
      <h2 class="card-header">Sublocations</h2>
      <div class="card-body">
        <ul class="list-group list-group-flush p-0 m-0">
          <li class="list-group-item p-1"
              v-for="subLocation in organisation.flat_sub_locations" :key="subLocation.id">
            <div class="d-flex justify-content-between align-items-center">
              <!-- Wide screen -->
              <div class="d-none d-md-block me-2">
                {{ subLocation.location.code }}.<strong>{{ subLocation.code }}</strong> - {{ subLocation.name }}
              </div>
              <!-- Small screen -->
              <div class="d-block d-md-none me-2">
                {{ subLocation.location.code }}.<strong>{{ subLocation.code }}</strong>
              </div>
              <div>
                <button class="btn btn-primary btn-sm me-2" v-if="isAdmin" @click="editSubLocation(subLocation)"><i
                    class="fas fa-pen"></i></button>
                <button class="btn btn-danger btn-sm me-2" v-if="isAdmin"
                        @click="confirmDeleteSubLocation(subLocation.id)"><i
                    class="fas fa-trash"></i></button>
                <button class="btn btn-primary btn-sm" v-if="isAdmin" @click="createFunctionModal(subLocation)"><i
                    class="fas fa-plus"></i> Function
                </button>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <CreateSubLocationModal
        v-if="showCreateSubLocationModal"
        :locationId="selectedLocation.id"
        @close="showCreateSubLocationModal = false"
        @create-sub-location="addSubLocation"
    />

    <EditSubLocationModal
        v-if="showEditSubLocationModal"
        :subLocation="selectedSubLocation"
        :locations="organisation.sites.flatMap(site => site.locations)"
        @close="showEditSubLocationModal = false"
        @save-sub-location="updateSubLocation"
    />

    <!-- Functions -->
    <div class="card mb-3" v-if="organisation.flat_functions && organisation.flat_functions.length>0">
      <h2 class="card-header">Functions</h2>
      <div class="card-body">
        <ul class="list-group list-group-flush p-0 m-0">
          <li class="list-group-item p-1"
              v-for="thisFunction in organisation.flat_functions" :key="thisFunction.id">
            <div class="d-flex justify-content-between align-items-center">
              <!-- Wide screen -->
              <div class="d-none d-md-block me-2">
                {{ thisFunction.sub_location.code }}.<strong>{{ thisFunction.code }}</strong> -
                {{ thisFunction.description }}
              </div>
              <!-- Small screen -->
              <div class="d-block d-md-none me-2">
                {{ thisFunction.sub_location.code }}.<strong>{{ thisFunction.code }}</strong>
              </div>
              <div>
                <button class="btn btn-primary btn-sm me-2" v-if="isAdmin" @click="editFunction(thisFunction)"><i
                    class="fas fa-pen"></i></button>
                <button class="btn btn-danger btn-sm" v-if="isAdmin"
                        @click="confirmDeleteFunction(thisFunction.id)"><i
                    class="fas fa-trash"></i></button>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <CreateFunctionModal
        v-if="showCreateFunctionModal"
        :subLocationId="selectedSubLocation.id"
        @close="showCreateFunctionModal = false"
        @create-function="addFunction"
    />
    <EditFunctionModal
        v-if="showEditFunctionModal"
        :function="selectedFunction"
        :subLocations="organisation.flat_sub_locations"
        @close="showEditFunctionModal = false"
        @save-function="updateFunction"
    />

    <!-- Events -->
    <div class="card mb-3">
      <h2 class="card-header">Events</h2>
      <div class="card-body">
        <ul class="list-group list-group-flush p-0 m-0" v-if="organisation && organisation.events">
          <li class="list-group-item p-1"
              v-for="event in organisation.events.sort((a, b) => a.date.localeCompare(b.date))"
              :key="event.id">
            <div class="d-flex justify-content-between align-items-center">
              <!-- Wide screen -->
              <div class="d-none d-md-block me-2">
                <strong>{{ event.code }}</strong> -
                {{ event.name }} -
                {{ dayjs(event.date).local().format('ddd DD/MM/YY') }}
              </div>
              <!-- Small screen -->
              <div class="d-block d-md-none me-2">
                <strong>{{ event.code }}</strong> -
                {{ dayjs(event.date).local().format('ddd DD/MM/YY') }}
              </div>
              <div>
                <button class="btn btn-primary btn-sm me-2" v-if="isAdmin" @click="editEvent(organisation, event)"><i
                    class="fas fa-pen"></i></button>
                <button class="btn btn-danger btn-sm me-2" v-if="isAdmin" @click="confirmDeleteEvent(event.id)"><i
                    class="fas fa-trash"></i></button>
                <button class="btn btn-primary btn-sm" v-if="!organisation.timeslots_enabled && isAdmin"
                        @click="createShiftModal(event)"><i
                    class="fas fa-plus"></i> Shift
                </button>
                <button class="btn btn-primary btn-sm" v-if="organisation.timeslots_enabled && isAdmin"
                        @click="createTimeslotModal(event)"><i
                    class="fas fa-plus"></i> Timeslot
                </button>
              </div>
            </div>
            <EditEventModal
                v-if="showEditEventModal"
                :organisation="selectedOrganisation"
                :event="selectedEvent"
                @close="showEditEventModal = false"
                @save-event="updateEvent"
            />

          </li>
        </ul>
        <button class="btn btn-primary btn-sm" v-if="isAdmin" @click="showCreateEventModal = true">Add event</button>
      </div>
      <CreateEventModal
          v-if="showCreateEventModal"
          :organisation="organisation"
          @close="showCreateEventModal = false"
          @create-event="createEvent"
      />
      <EditShiftModal
          v-if="showEditShiftModal"
          :shift="selectedShift"
          :locations="organisation.sites.flatMap(site => site.locations)"
          :tags="tags"
          @close="showEditShiftModal = false"
          @save-shift="updateShift"
      />
    </div>

    <!-- Timeslots -->
    <div class="card mb-3" v-if="organisation.flat_timeslots && organisation.flat_timeslots.length>0">
      <h2 class="card-header">Timeslots</h2>
      <div class="card-body">
        <ul class="list-group list-group-flush p-0 m-0">
          <li class="list-group-item p-1"
              v-for="timeslot in organisation.flat_timeslots" :key="timeslot.id">
            <div class="d-flex justify-content-between align-items-center">
              <!-- Wide screen -->
              <div class="d-none d-md-block me-2">
                {{ timeslot.event.code }}.<strong>{{ timeslot.function.code }}</strong>.{{ timeslot.name }}
                [{{ dayjs(timeslot.start).local().format('ddd DD/MM/YY HH:mm') }} -
                {{ dayjs(timeslot.end).local().format('ddd DD/MM/YY HH:mm') }}]
                ({{ formatDuration(timeslot.default_slot_length) }} | {{ timeslot.quantity }}x)
              </div>
              <!-- Small screen -->
              <div class="d-block d-md-none me-2">
                {{ timeslot.event.code }}.<strong>{{ timeslot.function.code }}</strong>.{{ timeslot.name }}
                ({{ timeslot.quantity }}x)
              </div>
              <div>
                <button class="btn btn-primary btn-sm me-2" v-if="isAdmin" @click="editTimeslot(timeslot)"><i
                    class="fas fa-pen"></i></button>
                <button class="btn btn-primary btn-sm me-2" v-if="isAdmin" :disabled="timeslot.shift_count > 0"
                        @click="splitIntoTimeslotShifts(timeslot)">
                  <i class="fas fa-layer-group"></i>
                </button>
                <button class="btn btn-danger btn-sm me-2" v-if="isAdmin" :disabled="timeslot.shift_count === 0"
                        @click="confirmDeleteTimeslotShifts(timeslot)">
                  <i class="fas fa-layer-group"></i>
                </button>
                <button class="btn btn-danger btn-sm" v-if="isAdmin"
                        @click="confirmDeleteTimeslot(timeslot.id)"><i
                    class="fas fa-trash"></i></button>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <!-- Shifts -->
    <div class="card mb-3" v-if="hasShifts">
      <h2 class="card-header">Shifts</h2>
      <div class="card-body">
        <div v-for="event in organisation.events" :key="event.id">
          <ul class="list-group list-group-flush p-0 m-0">
            <li class="list-group-item p-1" v-for="shift in event.shifts" :key="shift.id">
              <div class="d-flex justify-content-between align-items-center">
                <!-- Wide screen -->
                <div class="d-none d-md-block me-2">
                  {{ shift.event.code }}.{{ shift.location.code }} - <strong>{{ shift.name }}</strong> -
                  {{ dayjs(shift.start).local().format('ddd DD/MM/YY HH:mm') }} -
                  {{ dayjs(shift.end).local().format('ddd DD/MM/YY HH:mm') }}
                  ({{ shift.tag }} ${{ shift.score }} | {{ shift.capacity }}x)
                </div>
                <!-- Small screen -->
                <div class="d-block d-md-none me-2">
                  {{ shift.event.code }}.{{ shift.location.code }}.<strong>{{ shift.name }}</strong> -
                  {{ dayjs(shift.start).local().format('DD/MM HH:mm') }}-{{ dayjs(shift.end).local().format('HH:mm') }}
                </div>
                <div>
                  <button class="btn btn-primary btn-sm me-2" v-if="isAdmin" @click="editShift(shift)"><i
                      class="fas fa-pen"></i></button>
                  <button class="btn btn-danger btn-sm" v-if="isAdmin" @click="confirmDeleteShift(shift.id)"><i
                      class="fas fa-trash"></i></button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Timeslot Shifts -->
    <div v-if="organisation && organisation.flat_timeslot_shifts && organisation.flat_timeslot_shifts.length !== 0"
         class="card mb-3">
      <h2 class="card-header">Timeslot shifts</h2>
      <div class="card-body">
        <ul class="list-group list-group-flush p-0 m-0">
          <li class="list-group-item p-1"
              v-for="timeslotShift in organisation.flat_timeslot_shifts" :key="timeslotShift.id">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                {{ timeslotShift.timeslot.event.code }}.<strong>{{ timeslotShift.timeslot.function.code }}</strong>.{{
                  timeslotShift.timeslot.name
                }}.{{ timeslotShift.name }} ({{ timeslotShift.timeslot.quantity }}x)
              </div>
              <button class="btn btn-danger btn-sm" v-if="isAdmin"
                      @click="confirmDeleteTimeslotShift(timeslotShift.id)"><i
                  class="fas fa-trash"></i></button>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <CreateTimeslotModal
        v-if="showCreateTimeslotModal"
        :event="selectedEvent"
        :locations="organisation.sites.flatMap(site => site.locations)"
        :functions="organisation.flat_functions"
        @close="showCreateTimeslotModal = false"
        @create-timeslot="createTimeslot"
    />
    <EditTimeslotModal
        v-if="showEditTimeslotModal"
        :timeslot="selectedTimeslot"
        :functions="organisation.flat_functions"
        @close="showEditTimeslotModal = false"
        @save-timeslot="updateTimeslot"
    />
  </div>

  <!-- Show a spinner whilst loading the large amount of organisation related data -->
  <div v-else class="d-flex justify-content-center align-items-center">
    <div class="spinner-grow m-5" role="status"/>
    <strong>Loading your organisation... </strong>
  </div>

</template>

<script>
import CreateSiteModal from "@/components/CreateSiteModal.vue";
import EditSiteModal from "@/components/EditSiteModal.vue";
import CreateLocationModal from "@/components/CreateLocationModal.vue";
import EditLocationModal from "@/components/EditLocationModal.vue";
import CreateSubLocationModal from "@/components/CreateSubLocationModal.vue";
import CreateEventModal from "@/components/CreateEventModal.vue";
import EditEventModal from "@/components/EditEventModal.vue";
import CreateShiftModal from "@/components/CreateShiftModal.vue";
import EditShiftModal from "@/components/EditShiftModal.vue";
import EditSubLocationModal from "@/components/EditSubLocationModal.vue";
import CreateFunctionModal from "@/components/CreateFunctionModal.vue";
import EditFunctionModal from "@/components/EditFunctionModal.vue";
import CreateTimeslotModal from "@/components/CreateTimeslotModal.vue";
import EditTimeslotModal from "@/components/EditTimeslotModal.vue";
import dayjs from "dayjs";
import {API_BASE_URL} from "@/config";
import {getCSRFToken} from "@/store/auth";

export default {
  name: 'OrganisationPage',
  components: {
    CreateFunctionModal,
    CreateSiteModal,
    EditSiteModal,
    CreateLocationModal,
    EditLocationModal,
    CreateSubLocationModal,
    EditSubLocationModal,
    EditFunctionModal,
    CreateTimeslotModal,
    EditTimeslotModal,
    CreateEventModal,
    EditEventModal,
    CreateShiftModal,
    EditShiftModal,
  },
  data() {
    return {
      organisation: null,
      sites: [],
      locations: [],
      events: [],
      showCreateModal: false,
      showCreateSiteModal: false,
      showEditSiteModal: false,
      showCreateLocationModal: false,
      showEditLocationModal: false,
      showCreateSubLocationModal: false,
      showEditSubLocationModal: false,
      showCreateFunctionModal: false,
      showEditFunctionModal: false,
      showCreateTimeslotModal: false,
      showEditTimeslotModal: false,
      showCreateEventModal: false,
      showEditEventModal: false,
      showCreateShiftModal: false,
      selectedOrganisation: null,
      selectedEvent: null,
      selectedSite: null,
      selectedLocation: null,
      selectedSubLocation: null,
      selectedFunction: null,
      selectedTimeslot: null,
      isAdmin: false,
      selectedShift: null,
      showEditShiftModal: false,
      tags: [],
    };
  },
  mounted() {
    this.fetchOrganisationDetails();
    this.fetchTags();
  },
  methods: {
    navigateToUserShiftsAdmin() {
      const url = this.$router.resolve({
        name: 'organisation-user-shifts-admin',
        params: {slug: this.$route.params.slug}
      }).href;
      window.open(url, '_blank');
      // this.$router.push({name: 'organisation-user-shifts-admin', params: {slug: this.$route.params.slug}});
    },
    navigateToGoodiesAdmin() {
      const url = this.$router.resolve({
        name: 'organisation-goodies-admin',
        params: {slug: this.$route.params.slug}
      }).href;
      window.open(url);
    },
    navigateToUserGoodiesAdmin() {
      const url = this.$router.resolve({
        name: 'organisation-user-goodies-admin',
        params: {slug: this.$route.params.slug}
      }).href;
      window.open(url);
    },
    navigateToUsers() {
      this.$router.push({name: 'organisation-users', params: {slug: this.$route.params.slug}});
    },
    navigateToRoles() {
      this.$router.push({name: 'organisation-roles', params: {slug: this.$route.params.slug}});
    },
    // createSiteModal(organisation) {
    //   this.selectedOrganisation = organisation;
    //   this.showCreateSiteModal = true;
    // },
    createLocationModal(site) {
      this.selectedSite = site;
      this.showCreateLocationModal = true;
    },
    createSubLocationModal(location) {
      this.selectedLocation = location;
      this.showCreateSubLocationModal = true;
    },
    createFunctionModal(subLocation) {
      this.selectedSubLocation = subLocation;
      this.showCreateFunctionModal = true;
    },
    createTimeslotModal(thisEvent) {
      this.selectedEvent = thisEvent;
      this.showCreateTimeslotModal = true;
    },
    // createEventModal() {
    //   this.showCreateEventModal = true;
    // },
    async fetchOrganisationDetails() {
      try {
        const response = await fetch(
            `${API_BASE_URL}/organisations/${this.$route.params.slug}`, {
              method: 'GET',
              credentials: 'include',
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.organisation = data.organisation;
          this.isAdmin = data.is_admin;
          document.title = `${data.organisation.name} - Admin`;
        }
      } catch (error) {
        console.error('Error fetching organisation details:', error);
      }
    },

    async fetchTags() {
      try {
        const response = await fetch(`${API_BASE_URL}/shift-tags`, {
              method: 'GET',
              credentials: 'include',
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.tags = data;
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    },

    // Sites
    async createSite(siteData) {
      const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      try {
        const response = await fetch(
            `${API_BASE_URL}/organisations/${this.$route.params.slug}/sites`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
              body: JSON.stringify(siteData),
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await response.json();
          this.showCreateModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error creating site:', error);
      }
    },
    async deleteSite(siteId) {
      try {
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        const response = await fetch(
            `${API_BASE_URL}/organisations/${this.$route.params.slug}/sites/${siteId}/delete`, {
              method: 'DELETE',
              credentials: 'include',
              headers: {
                'X-CSRFToken': csrfToken,
              }
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error deleting site:', error);
      }
    },
    async editSite(organisation, site) {
      this.selectedOrganisation = organisation;
      this.selectedSite = site;
      this.showEditSiteModal = true;
    },
    async updateSite(siteData) {
      try {
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        const response = await fetch(
            `${API_BASE_URL}/organisations/${this.$route.params.slug}/sites/${siteData.id}/edit`, {
              method: 'PUT',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
              body: JSON.stringify(siteData),
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          this.showEditSiteModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error updating site:', error);
      }
    },
    async addLocation(locationData) {
      const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      try {
        const response = await fetch(
            `${API_BASE_URL}/organisations/${this.$route.params.slug}/sites/${this.selectedSite.id}/locations`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
              body: JSON.stringify(locationData), // site: locationData.site_id,
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await response.json();
          this.showCreateLocationModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error creating site:', error);
      }
    },
    editLocation(location) {
      this.selectedLocation = location;
      this.showEditLocationModal = true;
    },
    async updateLocation(locationData) {
      try {
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        const response = await fetch(
            `${API_BASE_URL}/locations/${locationData.id}`, {
              method: 'PUT',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFTOKEN': csrfToken,
              },
              body: JSON.stringify(locationData),
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails();
          this.showEditLocationModal = false;
        }
      } catch (error) {
        console.error('Error updating location:', error);
      }
    },
    async deleteLocation(locationId) {
      const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      try {
        const response = await fetch(
            `${API_BASE_URL}/locations/${locationId}`, {
              method: 'DELETE',
              credentials: 'include',
              headers: {
                "X-CSRFTOKEN": csrfToken,
              }
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails();
        }
      } catch (error) {
        console.error('Error deleting location:', error);
      }
    },

    // SubLocations - Start

    async addSubLocation(subLocationData) {
      const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      try {
        const response = await fetch(
            `${API_BASE_URL}/locations/${this.selectedLocation.id}/sub-locations`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
              body: JSON.stringify(subLocationData), // site: locationData.site_id,
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await response.json();
          this.showCreateLocationModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error creating sub-location:', error);
      }
    },

    async updateSubLocation(subLocationData) {
      try {
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        const response = await fetch(
            `${API_BASE_URL}/sub-locations/${subLocationData.id}`, {
              method: 'PUT',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFTOKEN': csrfToken,
              },
              body: JSON.stringify(subLocationData),
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails();
          this.showEditLocationModal = false;
        }
      } catch (error) {
        console.error('Error updating sub-location:', error);
      }
    },

    async deleteSubLocation(id) {
      const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      try {
        const response = await fetch(
            `${API_BASE_URL}/sub-locations/${id}`, {
              method: 'DELETE',
              credentials: 'include',
              headers: {
                "X-CSRFTOKEN": csrfToken,
              }
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails();
        }
      } catch (error) {
        console.error('Error deleting sub-location:', error);
      }
    },

    editSubLocation(subLocation) {
      this.selectedSubLocation = subLocation;
      this.showEditSubLocationModal = true;
    },

    // SubLocations - End

    // Functions - Start

    async addFunction(functionData) {
      const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      try {
        const response = await fetch(
            `${API_BASE_URL}/sub-locations/${this.selectedSubLocation.id}/functions`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
              body: JSON.stringify(functionData),
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await response.json();
          this.showCreateLocationModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error creating function:', error);
      }
    },

    editFunction(thisFunction) {
      this.selectedFunction = thisFunction;
      this.showEditFunctionModal = true;
    }
    ,

    async deleteFunction(id) {
      const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      try {
        const response = await fetch(
            `${API_BASE_URL}/functions/${id}`, {
              method: 'DELETE',
              credentials: 'include',
              headers: {
                "X-CSRFTOKEN": csrfToken,
              }
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails();
        }
      } catch (error) {
        console.error('Error deleting function:', error);
      }
    },

    async updateFunction(functionData) {
      try {
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        const response = await fetch(
            `${API_BASE_URL}/functions/${functionData.id}`, {
              method: 'PUT',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFTOKEN': csrfToken,
              },
              body: JSON.stringify(functionData),
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails();
          this.showEditLocationModal = false;
        }
      } catch (error) {
        console.error('Error updating function:', error);
      }
    },


// Functions - End

// Events
    async createEvent(eventData) {
      try {
        // Fetch CSRF token
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        // Include organisation_id in eventData
        eventData.organisation_id = this.$route.params.id;

        // Make a POST request to create a new event
        const response = await fetch(
            `${API_BASE_URL}/organisations/${this.$route.params.slug}/events`,
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                "X-CSRFTOKEN": csrfToken,
              },
              body: JSON.stringify(eventData),
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.events = data.events;
          this.showCreateEventModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error creating event:', error);
      }
    }
    ,
    async editEvent(organisation, event) {
      this.selectedOrganisation = organisation;
      this.selectedEvent = event;
      this.showEditEventModal = true;
    }
    ,
    async updateEvent(eventData) {
      try {
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        const response = await fetch(
            `${API_BASE_URL}/organisations/${this.$route.params.slug}/events/${eventData.id}`, {
              method: 'PUT',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
              body: JSON.stringify(eventData),
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.sites = this.sites.map(site => site.id === data.id ? data : site);
          this.showEditEventModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error updating site:', error);
      }
    }
    ,
    async confirmDeleteEvent(eventId) {
      if (confirm("Are you sure you want to delete this event?")) {
        await this.deleteEvent(eventId);
      }
    }
    ,
    async confirmDeleteSite(siteId) {
      if (confirm("Are you sure you want to delete this site?")) {
        await this.deleteSite(siteId);
      }
    }
    ,
    async confirmDeleteLocation(locationId) {
      if (confirm("Are you sure you want to delete this location?")) {
        await this.deleteLocation(locationId);
      }
    }
    ,
    async confirmDeleteSubLocation(subLocationId) {
      if (confirm("Are you sure you want to delete this sub-location?")) {
        await this.deleteSubLocation(subLocationId);
      }
    }
    ,
    async confirmDeleteFunction(functionId) {
      if (confirm("Are you sure you want to delete this function?")) {
        await this.deleteFunction(functionId);
      }
    }
    ,
    async confirmDeleteShift(shiftId) {
      if (confirm("Are you sure you want to delete this shift?")) {
        await this.deleteShift(shiftId);
      }
    },
    async confirmDeleteTimeslot(timeslotId) {
      if (confirm("Are you sure you want to delete this timeslot?")) {
        await this.deleteTimeslot(timeslotId);
      }
    },
    async confirmDeleteTimeslotShifts(timeslot) {
      if (confirm("Are you sure you want to delete all timeslot shifts for this timeslot?")) {
        await this.deleteTimeslotShifts(timeslot);
      }
    },
    async confirmDeleteTimeslotShift(timeslotShiftId) {
      if (confirm("Are you sure you want to delete this timeslot shift?")) {
        await this.deleteTimeslotShift(timeslotShiftId);
      }
    },
    async deleteEvent(eventId) {
      try {
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        const response = await fetch(
            `${API_BASE_URL}/organisations/${this.$route.params.slug}/events/${eventId}`, {
              method: 'DELETE',
              credentials: 'include',
              headers: {
                'X-CSRFToken': csrfToken,
              }
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          // this.sites = this.sites.filter(site => site.id !== siteId);
          await this.fetchOrganisationDetails();

        }
      } catch (error) {
        console.error('Error deleting site:', error);
      }
    }
    ,
    createShiftModal(event) {
      this.selectedEvent = event;
      this.showCreateShiftModal = true;
    }
    ,

// Shifts

    async createShift(shiftData) {
      try {
        // Fetch CSRF token
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        // Make a POST request to create a new shift
        const response = await fetch(
                `${API_BASE_URL}/organisations/${this.$route.params.slug}/events/${this.selectedEvent.id}/shifts`,
                {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFTOKEN': csrfToken,
                  },
                  body: JSON.stringify(shiftData),
                }
            )
        ;
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.shifts = data.shifts;
          this.showCreateShiftModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch
          (error) {
        console.error('Error creating shift:', error);
      }
    },

    getLocationName(locationId) {
      for (const site of this.organisation.sites) {
        const location = site.locations.find(location => location.id === locationId);
        if (location) {
          return location.name;
        }
      }
      return 'Unknown Location';
    },

    async deleteShift(shiftId) {
      try {
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;
        const response = await fetch(
            `${API_BASE_URL}/shifts/${shiftId}`, {
              method: 'DELETE',
              credentials: 'include',
              headers: {
                'X-CSRFToken': csrfToken,
              }
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails();
        }
      } catch (error) {
        console.error('Error deleting shift:', error);
      }
    },

    async editShift(shift) {
      this.selectedShift = shift;
      this.showEditShiftModal = true;
    },

    async updateShift(shiftData) {
      try {
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        const response = await fetch(
            `${API_BASE_URL}/shifts/${shiftData.id}`, {
              method: 'PUT',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
              body: JSON.stringify(shiftData),
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          this.showEditShiftModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error updating shift:', error);
      }
    },


    // Timeslots - Start

    async createTimeslot(timeslotData) {
      try {
        // Fetch CSRF token
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        // Make a POST request to create a new timeslot
        const response = await fetch(
                `${API_BASE_URL}/organisations/${this.$route.params.slug}/events/${this.selectedEvent.id}/timeslots`,
                {
                  method: 'POST',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFTOKEN': csrfToken,
                  },
                  body: JSON.stringify(timeslotData),
                }
            )
        ;
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.shifts = data.shifts;
          this.showCreateShiftModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch
          (error) {
        console.error('Error creating timeslot:', error);
      }
    },

    async editTimeslot(timeslot) {
      this.selectedTimeslot = timeslot;
      this.showEditTimeslotModal = true;
    },

    async splitIntoTimeslotShifts(timeslot) {
      try {
        const response = await fetch(
            `${API_BASE_URL}/timeslots/${timeslot.id}/split`,
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken(),
              }
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          this.showEditShiftModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error updating shift:', error);
      }
    },


    async updateTimeslot(timeslotData) {
      try {
        const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;

        const response = await fetch(
            `${API_BASE_URL}/timeslots/${timeslotData.id}`, {
              method: 'PUT',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
              },
              body: JSON.stringify(timeslotData),
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          this.showEditShiftModal = false;
          await this.fetchOrganisationDetails()
        }
      } catch (error) {
        console.error('Error updating shift:', error);
      }
    },


    async deleteTimeslot(id) {
      const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      try {
        const response = await fetch(
            `${API_BASE_URL}/timeslots/${id}`, {
              method: 'DELETE',
              credentials: 'include',
              headers: {
                "X-CSRFTOKEN": csrfToken,
              }
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails();
        }
      } catch (error) {
        console.error('Error deleting timeslot:', error);
      }
    },

    formatDuration(duration) {
      const [hours, minutes] = duration.split(':');
      return `${hours}H${minutes}`;
    },

    // Timeslots - End

    // TimeslotShifts - Start
    async deleteTimeslotShift(id) {
      const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      try {
        const response = await fetch(
            `${API_BASE_URL}/timeslot-shifts/${id}`, {
              method: 'DELETE',
              credentials: 'include',
              headers: {
                "X-CSRFTOKEN": csrfToken,
              }
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails();
        }
      } catch (error) {
        console.error('Error deleting timeslot shift:', error);
      }
    },

    async deleteTimeslotShifts(timeslot) {
      const csrfResponse = await fetch(`${API_BASE_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;
      try {
        const response = await fetch(
            `${API_BASE_URL}/timeslots/${timeslot.id}/shifts`, {
              method: 'DELETE',
              credentials: 'include',
              headers: {
                "X-CSRFTOKEN": csrfToken,
              }
            }
        );
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchOrganisationDetails();
        }
      } catch (error) {
        console.error('Error deleting timeslot shifts:', error);
      }
    }

    // TimeslotShifts - End


  }
  ,
  computed: {
    dayjs() {
      return dayjs
    }
    ,
    sortedLocations() {
      if (!this.organisation || !this.organisation.sites) {
        return [];
      }
      return this.organisation.sites
          .flatMap(site => site.locations.map(location => ({...location, site})))
          .sort((a, b) => {
            if (a.site.code < b.site.code) return -1;
            if (a.site.code > b.site.code) return 1;
            if (a.code < b.code) return -1;
            if (a.code > b.code) return 1;
            return 0;
          });
    }
    ,
    hasShifts() {
      return this.organisation && this.organisation.events.some(event => event.shifts && event.shifts.length > 0);
    }
  }
  ,
}
</script>